import { WORKING_WEEKS_WEEKDAY_START_MESSAGE } from "../dateStrategy/workingWeeksStartDate";

export const PRICELIST_VALIDATION_MESSAGES = {
  weekBracketsConsecutive: "Please ensure all Week Brackets are consecutive.",
  weekBracketsOverlap: "Please ensure Week Brackets do not overlap.",
  dateRangesOverlap: "Please ensure date ranges do not overlap.",
  dateRangesConsecutive: "Please ensure all date ranges are consecutive.",
  fillRequiredFields: "Please fill out all required fields.",
  applicableProductsRequired: "Enter Applicable Products to continue.",
  workingWeeksWeekdayStart: WORKING_WEEKS_WEEKDAY_START_MESSAGE,
} as const;

export function mapPricelistApiErrorMessage(message: string): string {
  const lower = message.toLowerCase();

  if (lower.includes("price periods cannot overlap")) {
    return PRICELIST_VALIDATION_MESSAGES.dateRangesOverlap;
  }

  if (lower.includes("price periods must be consecutive")) {
    return PRICELIST_VALIDATION_MESSAGES.dateRangesConsecutive;
  }

  if (lower.includes("price bands must be consecutive")) {
    return PRICELIST_VALIDATION_MESSAGES.weekBracketsConsecutive;
  }

  if (lower.includes("price bands cannot overlap")) {
    return PRICELIST_VALIDATION_MESSAGES.weekBracketsOverlap;
  }

  if (lower.includes("applicableproducts must contain at least one")) {
    return PRICELIST_VALIDATION_MESSAGES.applicableProductsRequired;
  }

  if (
    lower.includes("saturday and sunday are not allowed") &&
    lower.includes("working weeks")
  ) {
    return PRICELIST_VALIDATION_MESSAGES.workingWeeksWeekdayStart;
  }

  return message;
}

export function mapPricelistApiErrors(errors: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const error of errors) {
    const mapped = mapPricelistApiErrorMessage(error);
    if (!seen.has(mapped)) {
      seen.add(mapped);
      result.push(mapped);
    }
  }

  return result;
}

export function formatPricelistApiErrors(
  errors: string[],
  separator: string = "\n"
): string {
  const flattened = mapPricelistApiErrors(errors).flatMap((error) =>
    error
      .split("; ")
      .map((part) => part.trim())
      .filter(Boolean)
  );
  return flattened.join(separator);
}

export function getPricelistValidationSaveMessage(
  formErrors: { name?: string; error?: string }[] | null | undefined
): string {
  if (!formErrors?.length) {
    return PRICELIST_VALIDATION_MESSAGES.fillRequiredFields;
  }

  const explicitBlocker = formErrors
    .map((e) => String(e.error ?? "").trim())
    .find((msg) => msg.toLowerCase().startsWith("cannot save."));
  if (explicitBlocker) {
    return explicitBlocker;
  }

  const uniqueErrors = Array.from(
    new Set(
      formErrors
        .map((e) => String(e.error ?? "").trim())
        .filter(Boolean)
    )
  );
  if (uniqueErrors.length === 1) {
    return uniqueErrors[0];
  }

  if (formErrors.some((e) => String(e.name ?? "") === "dateStrategyAssignments")) {
    return uniqueErrors[0] ?? PRICELIST_VALIDATION_MESSAGES.fillRequiredFields;
  }

  if (
    formErrors.every((e) =>
      String(e.name ?? "").includes("applicableProducts")
    )
  ) {
    return PRICELIST_VALIDATION_MESSAGES.applicableProductsRequired;
  }

  const weekBracketOnly = formErrors.every((e) => {
    const name = String(e.name ?? "");
    return name.startsWith("minUnits-") || name.startsWith("maxUnits-");
  });
  if (weekBracketOnly) {
    const unique = new Set(
      formErrors.map((e) => e.error).filter((msg): msg is string => !!msg)
    );
    if (unique.size === 1) {
      return [...unique][0];
    }
  }

  if (formErrors.some((e) => String(e.name ?? "").startsWith("fee-"))) {
    return PRICELIST_VALIDATION_MESSAGES.fillRequiredFields;
  }

  return PRICELIST_VALIDATION_MESSAGES.fillRequiredFields;
}